import React, { useState, useEffect } from 'react';
import GatewayForm from './GatewayForm';
import GatewayList from './GatewayList';

const GatewayManagement = () => {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);

  useEffect(() => {
    fetchGateways();
  }, []);

  const fetchGateways = async () => {
    try {
      const response = await fetch('/api/gateways', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch gateways');
      const data = await response.json();
      setGateways(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingGateway(null);
    setShowForm(true);
  };

  const handleEdit = (gateway) => {
    setEditingGateway(gateway);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this gateway?')) {
      return;
    }

    try {
      const response = await fetch(`/api/gateways/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete gateway');
      
      await fetchGateways();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingGateway(null);
    fetchGateways();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingGateway(null);
  };

  if (loading) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-600">Loading gateways...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Gateway Management</h2>
          <p className="text-gray-600 mt-2">
            Manage your NMI payment gateway configurations
            {gateways.length > 0 && (
              <span className="ml-2 text-primary-600 font-medium">
                • Total: {gateways.length} gateway{gateways.length !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:ring-4 focus:ring-primary-100 transition-smooth"
        >
          + Add Gateway
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">❌ {error}</p>
        </div>
      )}

      {/* Gateway Form Modal */}
      {showForm && (
        <GatewayForm
          gateway={editingGateway}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      {/* Gateway List */}
      {gateways.length > 0 ? (
        <GatewayList
          gateways={gateways}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500 mb-4">No gateways configured yet</p>
          <button
            onClick={handleAddNew}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Add your first gateway →
          </button>
        </div>
      )}
    </div>
  );
};

export default GatewayManagement;
